import React from 'react';
import { Link } from 'react-router-dom';
import { Shield } from 'lucide-react';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-[#F5F8FF]">
      {/* Header */}
      <header className="border-b border-[#E8EDF5] bg-[#F5F8FF]/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <Shield className="h-8 w-8 text-[#1B5AFF]" />
              <span className="text-xl font-bold text-[#0A0F1E]">VETT</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-4xl font-bold text-[#0A0F1E] mb-2">Terms of Service</h1>
          <p className="text-[#5B6472] mb-8">Last Updated: March 6, 2026</p>

          <div className="prose prose-invert max-w-none space-y-8 text-[#0A0F1E]">
            <section>
              <h2 className="text-2xl font-semibold text-[#0A0F1E] mb-4">Acceptance of Terms</h2>
              <p>
                By creating an account on VETT, you agree to be bound by these Terms of Service and our <Link to="/privacy" className="text-[#1B5AFF] hover:underline">Privacy Policy</Link>. If you do not agree to these terms, do not use VETT.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#0A0F1E] mb-4">Description of Service</h2>
              <p>
                VETT is a platform that enables employers to create verified work records for current and former employees, and enables employers to discover and reach out to talent directly. The platform provides:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Verified employment records submitted by employers</li>
                <li>Employee profiles with skills and work history</li>
                <li>Talent search and discovery for employers</li>
                <li>Direct messaging between employers and candidates</li>
                <li>Performance review management</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#0A0F1E] mb-4">Account Registration</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>You must provide accurate and complete information when creating an account.</li>
                <li>You are responsible for maintaining the security of your account credentials.</li>
                <li>You must notify us immediately of any unauthorized access to your account.</li>
                <li>You must be 18 years of age or older to use VETT.</li>
                <li>One person or company per account. Sharing accounts is not permitted.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#0A0F1E] mb-4">Employer Obligations</h2>
              <p>As an employer using VETT, you agree to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Submit only truthful and accurate information in VETT Records.</li>
                <li>Not submit false, misleading, or defamatory information about any employee.</li>
                <li>Ensure your use of VETT complies with applicable employment laws in your jurisdiction, including but not limited to anti-discrimination laws.</li>
                <li>Obtain any necessary consents before submitting employment records.</li>
              </ul>
              <p className="mt-4 text-[#1B5AFF]">
                Submitting false or defamatory information is a violation of these terms and may result in immediate account termination and legal liability.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#0A0F1E] mb-4">Employee Rights</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>Employees have the right to review any VETT Record submitted about them before it is published.</li>
                <li>Employees have the right to flag fields they dispute and request discussion with the employer.</li>
                <li>Employees own their own professional data on VETT.</li>
                <li>Employers cannot publish a VETT Record without employee acknowledgment.</li>
                <li>Employees can control their visibility status and who can see their profile.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#0A0F1E] mb-4">Data Ownership</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>Employees own their VETT Records and professional data.</li>
                <li>If an employer account is closed, their submitted records remain accessible to the employee.</li>
                <li>Employees may request deletion of their records at any time by contacting <a href="mailto:contact@getvett.org" className="text-[#1B5AFF] hover:underline">contact@getvett.org</a>.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#0A0F1E] mb-4">Payment Terms</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>Employer subscriptions are billed monthly.</li>
                <li>The 3-day free trial requires no credit card.</li>
                <li>After the trial ends, access downgrades to the free tier unless a subscription is activated.</li>
                <li>No refunds are provided on monthly subscriptions.</li>
                <li>Annual subscriptions (when available) are eligible for a pro-rated refund within 7 days of payment.</li>
                <li>All payment processing is handled by Stripe.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#0A0F1E] mb-4">Intellectual Property</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>VETT owns all platform code, design, trademarks, and infrastructure.</li>
                <li>Users own their own data and content.</li>
                <li>You grant VETT a non-exclusive license to display and transmit your data solely to provide the service.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#0A0F1E] mb-4">Prohibited Uses</h2>
              <p>You agree not to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Scrape or use automated data collection tools on the platform.</li>
                <li>Create fake accounts or submit records for people without their knowledge.</li>
                <li>Harass or abuse other users.</li>
                <li>Upload malicious code or files.</li>
                <li>Use VETT to discriminate against employees based on protected characteristics.</li>
                <li>Attempt to circumvent any security measures.</li>
                <li>Use the platform for any illegal purpose.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#0A0F1E] mb-4">Limitation of Liability</h2>
              <p>
                VETT is provided "as is" without warranties of any kind, either express or implied.
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>We are not liable for the accuracy of employer-submitted records.</li>
                <li>We are not liable for hiring decisions made based on VETT data.</li>
                <li>Our total liability to you for any claim is limited to the amount you paid us in the 3 months preceding the claim.</li>
                <li>We are not liable for any indirect, incidental, special, or consequential damages.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#0A0F1E] mb-4">Indemnification</h2>
              <p>
                You agree to indemnify and hold harmless VETT, its officers, directors, employees, and agents from any claims, damages, losses, or expenses (including reasonable attorney fees) arising from:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Your use of the platform</li>
                <li>Your violation of these Terms</li>
                <li>Your violation of any third party rights</li>
                <li>Content you submit to the platform</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#0A0F1E] mb-4">Termination</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>Either party may terminate the account at any time.</li>
                <li>We may terminate accounts that violate these terms without notice.</li>
                <li>On termination, employer access is revoked immediately.</li>
                <li>Employee data is retained for 30 days, then deleted unless the employee requests earlier deletion.</li>
                <li>Employees can export their data before account deletion.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#0A0F1E] mb-4">FCRA Compliance Obligations (US Employers)</h2>
              
              <h3 className="text-lg font-medium text-white mt-6 mb-3">12.1 Consumer Reporting Agency Status</h3>
              <p>
                VETT operates as a consumer reporting agency (CRA) as defined under the Fair Credit Reporting Act, 15 U.S.C. § 1681 et seq. (FCRA). VETT records constitute "consumer reports" under FCRA when used in connection with employment decisions.
              </p>

              <h3 className="text-lg font-medium text-white mt-6 mb-3">12.2 Permissible Purpose</h3>
              <p>
                Employers may only access VETT records for a permissible purpose as defined by FCRA § 1681b, including: evaluating a candidate for employment, promotion, reassignment, or retention. Accessing VETT records for any other purpose is prohibited and may constitute a violation of federal law.
              </p>

              <h3 className="text-lg font-medium text-white mt-6 mb-3">12.3 Adverse Action Obligations</h3>
              <p>
                Before taking any adverse employment action (including declining to hire, failing to promote, or terminating employment) based in whole or in part on information obtained from VETT, the Employer must:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-2">
                <li>(a) provide the affected individual with a pre-adverse action notice including a copy of the VETT record relied upon and a written description of the individual's rights under FCRA;</li>
                <li>(b) allow a reasonable period (minimum five business days) for the individual to dispute the accuracy of the record; and</li>
                <li>(c) if adverse action is taken, provide a final adverse action notice identifying VETT as the source of the consumer report.</li>
              </ul>
              <p className="mt-4">
                VETT provides a template adverse action notice in the Employer Help Centre. Compliance with these requirements is solely the Employer's responsibility.
              </p>

              <h3 className="text-lg font-medium text-white mt-6 mb-3">12.4 Dispute Resolution</h3>
              <p>
                VETT maintains a dispute resolution mechanism that satisfies FCRA's reinvestigation requirements. Employers must not suppress, delete, or alter records subject to an active dispute.
              </p>

              <h3 className="text-lg font-medium text-white mt-6 mb-3">12.5 Record Retention</h3>
              <p>
                Employers must retain records of all FCRA-required notices for a minimum of five (5) years from the date of the employment decision.
              </p>

              <h3 className="text-lg font-medium text-white mt-6 mb-3">12.6 Indemnification</h3>
              <p>
                Employer agrees to indemnify, defend, and hold VETT harmless from any claims, damages, fines, or penalties arising from Employer's failure to comply with FCRA obligations, including failure to provide adverse action notices.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#0A0F1E] mb-4">Governing Law</h2>
              <p>
                These Terms of Service are governed by and construed in accordance with the laws of the State of Delaware, USA, without regard to its conflict of law principles.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#0A0F1E] mb-4">Dispute Resolution</h2>
              <p>
                Any dispute arising from these Terms or your use of VETT will be resolved by binding arbitration under the rules of the American Arbitration Association (AAA). The arbitration will be conducted in Delaware, USA.
              </p>
              <p className="mt-4">
                <strong>You waive the right to participate in class action lawsuits against VETT.</strong>
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#0A0F1E] mb-4">Changes to Terms</h2>
              <p>
                We may update these Terms of Service from time to time. We will notify users by email at least 30 days before any material changes take effect. Your continued use of VETT after the changes take effect constitutes acceptance of the new terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#0A0F1E] mb-4">Contact</h2>
              <p>
                If you have any questions about these Terms of Service, please contact us at:
              </p>
              <p className="mt-2">
                <a href="mailto:contact@getvett.org" className="text-[#1B5AFF] hover:underline">contact@getvett.org</a>
              </p>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#E8EDF5] py-8 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-[#1B5AFF]" />
              <span className="text-[#5B6472]">© 2026 VETT. All rights reserved.</span>
            </div>
            <div className="flex gap-6 text-sm">
              <Link to="/privacy" className="text-[#5B6472] hover:text-white">Privacy Policy</Link>
              <Link to="/terms" className="text-white">Terms of Service</Link>
              <Link to="/cookies" className="text-[#5B6472] hover:text-white">Cookie Policy</Link>
              <Link to="/impressum" className="text-[#5B6472] hover:text-white">Impressum</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
